import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { checkForMilestone, getTotalEmitted } from '../utils/emissionSchedule';
import { generateMilestoneTweet } from '../utils/shareUtils';
import ShareButton from './ShareButton';
import './MilestoneAlert.css';

function MilestoneAlert() {
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    // Check for milestone every 2 seconds
    const interval = setInterval(() => {
      const reachedPhase = checkForMilestone();
      if (reachedPhase) {
        setMilestone(reachedPhase);
        triggerConfetti();
        
        // Auto-dismiss after 10 seconds
        setTimeout(() => {
          setMilestone(null);
        }, 10000);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const triggerConfetti = () => {
    // Trigger confetti if available
    if (typeof window.confetti === 'function') {
      window.confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#22d3ee', '#a855f7', '#FFD700', '#ec4899']
      });
      
      // Second burst
      setTimeout(() => {
        window.confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.7 },
          colors: ['#22d3ee', '#a855f7', '#FFD700']
        });
      }, 300);
    }
  };

  const handleClose = () => {
    setMilestone(null);
  };

  if (!milestone) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="milestone-alert-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="milestone-alert-content"
          initial={{ scale: 0.5, opacity: 0, y: -50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ 
            type: 'spring', 
            damping: 15, 
            stiffness: 200 
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Celebration Icon */}
          <motion.div 
            className="milestone-icon"
            animate={{ 
              rotate: [0, -10, 10, -10, 10, 0],
              scale: [1, 1.1, 1, 1.1, 1]
            }}
            transition={{ 
              duration: 0.8,
              repeat: Infinity,
              repeatDelay: 1
            }}
          >
            🎉
          </motion.div>

          {/* Title */}
          <motion.h2 
            className="milestone-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            MILESTONE REACHED!
          </motion.h2>

          {/* Phase Info */}
          <motion.div 
            className="milestone-phase"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Phase {milestone.phase}: {milestone.name}
          </motion.div>

          {/* Message */}
          <motion.div 
            className="milestone-message"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            The community has unlocked the next emission phase!
          </motion.div>

          {/* New Rate */}
          <motion.div 
            className="milestone-rate"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, type: 'spring' }}
          >
            New Rate: <span className="rate-highlight">{milestone.rate} $DISTIL</span> per purge
          </motion.div>

          {/* Share Button */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55 }}
            style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}
          >
            <ShareButton
              tweetText={generateMilestoneTweet({
                phase: milestone.phase,
                phaseName: milestone.name,
                newRate: milestone.rate,
                totalEmitted: getTotalEmitted()
              })}
              variant="outline"
              size="medium"
              fullWidth={true}
              label="Share This Milestone!"
            />
          </motion.div>

          {/* Close Button */}
          <motion.button
            className="milestone-close-btn"
            onClick={handleClose}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Awesome! 🚀
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default MilestoneAlert;
